import { useCallback } from 'react';
import { TFilter, TModifier } from 'shared/types/filters';
import { useAppDispatch } from './useAppDispatch';
import { setJsonQuery } from 'shared/slices/jsonQuery';
import { modifyJsonQuery } from 'shared/utils/jsonQuery';
import { useSelector, useStore } from 'react-redux';
import { selectJsonQuery } from 'shared/selectors/jsonQuery';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { updateSearchMeta } from 'shared/thunks/searchMeta';
import { applyFilters } from 'shared/thunks/filters';
import { setSearchMetaTooltipAvailable, updateLastAppliedFilter } from 'shared/slices/searchMetaTooltip';
import { updateTags } from 'shared/thunks/tags';
import { updateLocalStorageFiltersWereApplied } from 'shared/utils/uxfbLocalStorage/uxfbLocalStorage';
import { selectQuerystring } from 'shared/selectors/searchMeta/selectQuerystring';
import { getRemovedQueryParams } from 'shared/utils/string';
import { QUICK_FILTERS_KEYS, TQuickFilterKey, trackQuickFilterApply, trackRemoveAllTags } from 'shared/utils/analytics';
import { IPreloadedState } from 'shared/types/redux';
import { selectSearchMetaData } from 'shared/selectors/searchMeta';

export const useFilterChangeCallback = (filterKey: TFilter = 'advancedFilters') => {
  const dispatch = useAppDispatch();
  const jsonQuery = useSelector(selectJsonQuery);
  const store = useStore();
  const queryString = useSelector(selectQuerystring);
  const searchMeta = useSelector(selectSearchMetaData);

  const {
    custom: { applyFilterAfterChange },
  } = useApplicationContext();

  return useCallback(
    async (...modifiers: [TModifier, ...TModifier[]]) => {
      let oldQueryString = queryString;
      if (!searchMeta && modifiers.some(({ action }) => action === 'clearTags')) {
        await dispatch(updateSearchMeta());
        oldQueryString = selectQuerystring(store.getState() as IPreloadedState);
      }

      const nextJsonQuery = modifyJsonQuery(jsonQuery, modifiers);

      dispatch(setJsonQuery(nextJsonQuery));

      await dispatch(updateSearchMeta());

      if (modifiers.some(({ action }) => action === 'clearTags')) {
        const newQueryString = selectQuerystring(store.getState() as IPreloadedState);
        const removedQueryString = getRemovedQueryParams(oldQueryString, newQueryString);
        trackRemoveAllTags(removedQueryString);
      }

      if (QUICK_FILTERS_KEYS.includes(filterKey)) {
        const queryString = selectQuerystring(store.getState() as IPreloadedState);
        trackQuickFilterApply(filterKey as TQuickFilterKey, queryString);
      }

      if (filterKey !== 'advancedFilters') {
        updateLocalStorageFiltersWereApplied();

        if (applyFilterAfterChange) {
          dispatch(updateTags());
          dispatch(applyFilters());

          return;
        }

        dispatch(setSearchMetaTooltipAvailable(true));
      }

      dispatch(updateLastAppliedFilter(filterKey));
    },
    [searchMeta, jsonQuery, dispatch, filterKey, store, queryString, applyFilterAfterChange],
  );
};
